import { IDLE_RATE_MAX_CENTS_PER_MIN } from './pricingCeilings.js';
/**
 * Residential host fee rules — the idle fee bounds, the billing floor,
 * and the retired plug-in fee cap.
 *
 * These live in @evi/shared (not @evi/billing, and not in the route or
 * the page) because the PWA renders them and depends on shared, never
 * on billing: the client and the server must check against the SAME
 * number. A cap the UI computes differently from the guard is a cap
 * that produces a rejection the host cannot see coming, which is the
 * failure mode the guest-window saga spent seven test cycles on.
 *
 * Nothing here is multifamily. A multifamily guest pays a markup on
 * the building's rate and its fees are operator-set in the admin app;
 * these bounds are for the resident-managed home branch only.
 *
 * THE PLUG-IN FEE IS RETIRED (Tier 2, 2026-09-26). The host no longer
 * sets a fee: the connection fee is EVI's, per charger, and its rulings
 * and numbers live in ./connectionFee.ts. The 2026-09-16 ruling recorded
 * here before — the host's fee defaulting to $1 and splitting 90/10
 * like energy — is SUPERSEDED. The cap functions below stay only because
 * a protected billing test pins them and legacy tariffs may still carry
 * a host-set rates.flat_fee that bills on sessions started before the
 * fee was pinned; no write path uses them any more.
 */
/**
 * The session size the plug-in fee is measured against: a typical
 * overnight home charge (Warren's ruling 2026-09-16, replacing the
 * 10 kWh top-up the first cut was sized on — a real host at 30¢ wanted
 * $1.50, which is 20% of an overnight session, and the top-up cap
 * refused it).
 *
 * Tunable. Change it here and both the ceiling shown in the editor and
 * the server guard move together.
 */
export const TYPICAL_HOME_SESSION_KWH = 25;
/**
 * Divisor on the typical session's energy charge. At 5, the plug-in
 * fee can never be more than a fifth of what a typical overnight guest
 * pays for the electricity itself — the fee stays a fee and cannot
 * quietly become the price.
 */
export const PLUG_IN_FEE_CAP_DIVISOR = 5;
/**
 * The kWh-equivalence ceiling: the largest plug-in fee a host may set
 * against a given energy rate.
 *
 *   max = round(TYPICAL_HOME_SESSION_KWH x energy_rate_cents / 5)
 *       = energy_rate_cents x 5
 *
 * At $0.30/kWh that is $1.50. At $0.15/kWh it is $0.75. The ceiling
 * scales with the host's own price, so a cheap charger cannot carry an
 * expensive fee.
 *
 * Returns 0 when the energy rate is missing or non-positive — a host
 * with no guest price set has no ceiling to price against, and 0 means
 * every non-zero fee is rejected until they set one.
 */
export function maxPlugInFeeCents(energyRateCents) {
    if (typeof energyRateCents !== 'number' || !Number.isFinite(energyRateCents))
        return 0;
    if (energyRateCents <= 0)
        return 0;
    return Math.round((TYPICAL_HOME_SESSION_KWH * energyRateCents) / PLUG_IN_FEE_CAP_DIVISOR);
}
/**
 * Is this plug-in fee within the ceiling for this energy rate?
 *
 * REJECT, never clamp — a silently reduced fee is a host being charged
 * a different price than the one they typed, and they would have no
 * way to discover it short of reading a receipt.
 */
export function isPlugInFeeWithinCap(flatFeeCents, energyRateCents) {
    if (!Number.isFinite(flatFeeCents) || flatFeeCents < 0)
        return false;
    if (flatFeeCents === 0)
        return true;
    return flatFeeCents <= maxPlugInFeeCents(energyRateCents);
}
// The recommended host plug-in fee and its ten-minute yardstick were
// removed with the host fee itself (Tier 2): see ./connectionFee.ts for
// EVI's per-charger fee, DEFAULT_CONNECTION_FEE_CENTS ($1).
/**
 * Idle fee bounds, cents per idle minute.
 *
 * Ceiling of 200 (=$2.00/min, $120/hr) is deliberately far above any
 * sane setting: the idle fee's job is to move a car, and a host who
 * wants it to bite hard should be able to make it bite. The floor of 1
 * keeps "set" distinguishable from "unset" — 0 means no idle fee, and
 * the editor writes null rather than 0 for that.
 */
export const HOME_IDLE_RATE_MIN_CENTS = 1;
/** Warren, 2026-09-29: "$2 a minute maximum." The network-wide ceiling (pricingCeilings.ts) is this number. */
export const HOME_IDLE_RATE_MAX_CENTS = IDLE_RATE_MAX_CENTS_PER_MIN;
/**
 * Plug-in fee bounds, cents. The cap above is the real constraint;
 * this is the absolute schema-level bound so a malformed request
 * cannot land a five-figure fee even against an expensive rate.
 */
export const HOME_PLUG_IN_FEE_MIN_CENTS = 0;
export const HOME_PLUG_IN_FEE_MAX_CENTS = 2000;
// ── Billing floor ───────────────────────────────────────────────────
/**
 * A session shorter than this AND below the energy floor delivered
 * nothing, and must bill nothing at all — not the plug-in fee, not the
 * idle fee, not tax.
 *
 * The case this exists for: a guest plugs in, the handshake fails, the
 * charger reports a StartTransaction and a StopTransaction forty
 * seconds apart with ~0 kWh. Without a floor the plug-in fee bills in
 * full, so the guest pays a dollar for a charge they never got, and
 * the host's first review says so.
 *
 * BOTH conditions are required. A session with zero energy but three
 * hours of clock is not a failed handshake — it is a car occupying a
 * space — so duration alone must never trip the floor.
 *
 * That occupancy is NOT billed as idle today, though. The idle rule
 * (2026-09-27, @evi/billing computeIdleMinutes) bills idle only from
 * evidence of when charging stopped, taken from the charger's energy
 * series; a session that never charged has no such moment, so it bills 0
 * idle. Warren, 2026-09-27: "A CAR THAT ARRIVES FULL bills no idle.
 * Correct as built. Without evidence that charging stopped there is no
 * idle to bill, and a host who wants to price occupancy uses a per-hour
 * tariff, which is a different product and already exists." Idle is also
 * capped at IDLE_FEE_MAX_MINUTES (60) per session (pricingCeilings.ts).
 */
export const BILLING_FLOOR_SECONDS = 120;
/**
 * Energy below which a session counts as having delivered nothing.
 * 0.05 kWh is under a minute of Level 2 draw and comfortably above
 * meter noise / rounding on a charger that reported the same reading
 * at start and stop.
 */
export const BILLING_FLOOR_KWH = 0.05;
/**
 * True when the session delivered nothing and must not be billed.
 *
 * Applies network-wide, not only to homes: a connection fee for a
 * connection that delivered nothing is wrong at a commercial site too.
 */
export function isBelowBillingFloor(metrics) {
    const seconds = metrics.duration_minutes * 60;
    return seconds < BILLING_FLOOR_SECONDS && metrics.energy_kwh < BILLING_FLOOR_KWH;
}
//# sourceMappingURL=residentialFees.js.map